import React, { useState, useContext } from 'react'
import {
	Button,
	Card,
	CardBody,
	Col,
	FormGroup,
	Input,
	Label,
	Modal,
	Row
} from 'reactstrap'
import { SpinnerComponent } from 'react-element-spinner'

import { trigger } from '../../events/customEvents'
import AuctionTabsService from '../../services/AuctionTabsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'
import Enumeration from '../../enumeration'

/**
 * Modal for placing an auction on hold.
 * BRD: Confirmation popup with VIN, hold reason dropdown, notes, YES/Cancel buttons
 */
const HoldAuctionModal = ({ isOpen = false, auction = null, onClose, onSuccess }) =>
{
	const [isModalBusy, setIsModalBusy] = useState(false)
	const [holdReasonId, setHoldReasonId] = useState(Enumeration.AuctionHoldReasonTypes.Other)
	const [holdNotes, setHoldNotes] = useState('')
	const sessionContext = useContext(SessionContext)

	// Check if this is a live unit with bids - per BRD, show different title
	const bidCount = auction?.bidCount ?? auction?.bidsCount ?? auction?.bids ?? 0
	const hasBids = bidCount > 0
	const vin = auction?.vin || auction?.vehicleVin || ''

	const handleCancelModal = () =>
	{
		setHoldReasonId(Enumeration.AuctionHoldReasonTypes.Other)
		setHoldNotes('')
		if (typeof onClose === 'function')
		{
			onClose()
		}
	}

	const handlePlaceOnHold = () =>
	{
		setIsModalBusy(true)
		AuctionTabsService
			.placeOnHold(auction.auctionId, holdReasonId, holdNotes.trim(), sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsModalBusy(false)
				setHoldReasonId(Enumeration.AuctionHoldReasonTypes.Other)
				setHoldNotes('')
				sessionContext.updateSession(result.data.loginSession)

				trigger('raiseNotification', { title: 'Hold Placed', message: 'The auction has been placed on hold.', type: 'success', duration: 5 })
				if (typeof onSuccess === 'function')
				{
					onSuccess()
				}
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
			})
	}

	return (
		<Modal
			className="modal-dialog-centered"
			size="md"
			isOpen={isOpen}
		>
			<SpinnerComponent
				loading={isModalBusy}
				position="centered"
				message="Processing..."
			/>
			<Card className="mb-0">
				<CardBody>
					{/* BRD: Title shows VIN and bid status */}
					<div className="text-muted mb-3">
						<strong>
							{hasBids ? `VIN:${vin} has a bid on it` : `VIN:${vin}`}
						</strong>
					</div>
					
					{/* BRD: Simple confirmation message */}
					<p className="mb-2">
						Are you sure you would like to place this unit on hold?
					</p>

					<FormGroup>
						<Label for="holdReason">Hold Reason</Label>
						<Input
							type="select"
							id="holdReason"
							value={holdReasonId}
							onChange={(e) => setHoldReasonId(parseInt(e.target.value, 10))}
						>
							<option value={Enumeration.AuctionHoldReasonTypes.ConditionReview}>Condition Review</option>
							<option value={Enumeration.AuctionHoldReasonTypes.PricingReview}>Pricing Review</option>
							<option value={Enumeration.AuctionHoldReasonTypes.DealerRequest}>Dealer Request</option>
							<option value={Enumeration.AuctionHoldReasonTypes.OEMRequest}>OEM Request</option>
							<option value={Enumeration.AuctionHoldReasonTypes.Other}>Other</option>
						</Input>
					</FormGroup>

					<FormGroup>
						<Label for="holdNotes">Notes</Label>
						<Input
							type="textarea"
							id="holdNotes"
							rows="3"
							value={holdNotes}
							onChange={(e) => setHoldNotes(e.target.value)}
							placeholder="Enter notes (optional)..."
						/>
					</FormGroup>

					<Row>
						<Col className="text-center">
							<Button
								color="primary"
								className="me-2"
								onClick={handlePlaceOnHold}
							>
								YES
							</Button>
							<Button
								color="secondary"
								onClick={handleCancelModal}
							>
								Cancel
							</Button>
						</Col>
					</Row>
				</CardBody>
			</Card>
		</Modal>
	)
}

export default HoldAuctionModal
